import Button from 'components/Button';
import BaseModal from 'components/Modals/BaseModal/BaseModal';
import { logEvent } from 'config/tracking';
import BACKEND_URL from 'constants/backendUrl';
import { REGEXP } from 'constants/validationsForm';
import { useContacts, useInvalidateContacts } from 'core/api/contacts';
import { useInvalidateTasks } from 'core/api/tasks';
import { Civility } from 'core/enums/civility';
import ContactStatus from 'core/enums/contactStatus';
import { Politesse } from 'core/enums/politesse';
import { TaskPriority } from 'core/enums/taskPriority';
import { TaskSubject } from 'core/enums/taskSubject';
import Contact from 'core/models/contact';
import Tag, { NewTag } from 'core/models/tag';
import Task from 'core/models/task';
import { DisabledField, ModalType } from 'core/useCases/modal/types';
import { startOfTomorrow } from 'date-fns';
import useResponsive from 'hooks/responsive';
import { useIsPremium } from 'hooks/userIsPremium';
import { FC, useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useModalStore } from 'stores/modal';
import { SSpacedColumn } from 'styles/layout';
import { enumKeys } from 'utils/common';
import { GET, POST, PUT } from 'utils/http';
import { TrackingEventName } from 'utils/types/amplitude';
import { SelectInput } from '../lib';
import ResourcesInput from '../lib/ResourcesInput';
import { OptionsProps } from '../lib/SelectInput';
import TagsInput from '../lib/TagsInput';
import TextInput from '../lib/TextInput';

type Inputs = {
    firsName?: string;
    lastName: string;
    mobileNumber?: string;
    phoneNumber?: string;
    email?: string;
    job?: string;
    company?: string;
    parentId?: string;
    civility: Civility;
    politesse?: Politesse;
    comments?: string;
    tags?: Array<Tag | NewTag>;
};

type ContactFormProps = {
    closeModal(): void;
    defaultValues?: Partial<Contact>;
    disabledFields?: Array<DisabledField>;
};

type GetDescendantsResponse = {
    descendantIds: string[];
};

const getDescendantIdsRequest = async (id: string): Promise<string[]> => {
    const response = (await GET<GetDescendantsResponse>(
        `${BACKEND_URL}/api/contact/descendants/${id}`,
    )) as GetDescendantsResponse;

    return response.descendantIds;
};

type CreateContactResponse = {
    contact: Contact;
    task?: Task;
};

type UpdateContactResponse = {
    updatedContact: Contact;
    updatedRanks: Array<{
        id: string;
        rank: number;
    }>;
};

const createContactRequest = async (inputs: Inputs): Promise<CreateContactResponse> => {
    const response = (await POST<CreateContactResponse>(`${BACKEND_URL}/api/contact`, inputs)) as CreateContactResponse;

    return response;
};

const updateContactRequest = async (inputs: Inputs, id: string): Promise<UpdateContactResponse> => {
    const response = (await PUT<UpdateContactResponse>(`${BACKEND_URL}/api/contact/${id}`, {
        ...inputs,
    })) as UpdateContactResponse;

    return response;
};

const computeData = (contact?: Partial<Contact>): Partial<Inputs> => {
    // Default politesse to "Vouvoiement + Nom" when creating a new contact.
    if (!contact) {
        return { politesse: Politesse.VouvoiementLastName };
    }

    const { parent, id, ...computedData } = contact;
    (computedData as Inputs).parentId = parent?.id;

    // A contact being created (no id yet) with no politesse set gets the default.
    if (!id && !computedData.politesse) {
        (computedData as Inputs).politesse = Politesse.VouvoiementLastName;
    }

    return computedData;
};

const ContactForm: FC<ContactFormProps> = ({ closeModal, defaultValues, disabledFields }) => {
    const openModal = useModalStore((s) => s.openModal);
    const closeModalStore = useModalStore((s) => s.closeModal);
    const invalidateContacts = useInvalidateContacts();
    const invalidateTasks = useInvalidateTasks();
    const { t } = useTranslation();
    const { isMobile } = useResponsive();
    const { data: contactsData } = useContacts();
    const contactList = useMemo(() => contactsData ?? [], [contactsData]);
    const [parentOptions, setParentOptions] = useState<OptionsProps>([]);
    const [civilityOptions, setCivilityOptions] = useState<OptionsProps>([]);
    const [politesseOptions, setPolitesseOptions] = useState<OptionsProps>([]);
    const [descendantIds, setDescendantIds] = useState<string[]>([]);
    const isPremium = useIsPremium();
    const history = useHistory();

    const methods = useForm<Inputs>({
        defaultValues: computeData(defaultValues),
    });
    const { handleSubmit, control } = methods;

    const openFirstTaskModal = (newContact: Contact) => {
        // TODO Handle the default media type
        openModal({
            allowSwitchToEvent: true,
            cancelButtonLabel: t('common.ignore'),
            defaultValues: {
                contact: newContact,
                deadline: startOfTomorrow(),
                priority: TaskPriority.Normal,
                subject: TaskSubject.FirstContact,
            },
            disabledFields: [DisabledField.ContactId],
            title: t('task-list.add-first-task'),
            type: ModalType.Task,
        });
    };

    const openIncompleteContactCreatedModal = () => {
        openModal({
            defaultValues: {
                paragraph: t('contact.incomplete-contact-created.paragraph'),
            },
            title: t('contact.incomplete-contact-created.title'),
            type: ModalType.Information,
        });
    };

    const onSubmit = handleSubmit(async (values: Inputs) => {
        try {
            if (defaultValues?.id) {
                // Update
                const { updatedContact } = await updateContactRequest(values, defaultValues.id);

                // If the status change from incomplete to new, add first task
                if (ContactStatus.Incomplete === defaultValues?.status && updatedContact.status === ContactStatus.New) {
                    openFirstTaskModal(updatedContact);
                } else {
                    closeModalStore();
                }
            } else {
                // Creation
                const { contact, task } = await createContactRequest(values);

                if (task) {
                    await invalidateTasks();
                }
                // Add first task if new contact is new and not incomplete
                if (ContactStatus.Incomplete !== contact.status) {
                    openFirstTaskModal(contact);
                } else {
                    openIncompleteContactCreatedModal();
                }
                logEvent({
                    name: TrackingEventName.CONTACT_CREATED,
                    params: { contactId: contact.id, contactStatus: contact.status },
                });
            }

            // TODO: for contact deletion button recheck, fetch only updated data
            await invalidateContacts();

            toast.success(t(`contact-form.${defaultValues?.id ? 'update' : 'creation'}.success`));
        } catch {
            toast.error(t(`contact-form.${defaultValues?.id ? 'update' : 'creation'}.error`));
            closeModalStore();
        }
    });

    useEffect(() => {
        const civilityOptionsComputed: OptionsProps = [];
        for (const value of enumKeys(Civility)) {
            civilityOptionsComputed.push({
                label: t(`common.civility.enum.${Civility[value]}`),
                value: Civility[value],
            });
        }
        setCivilityOptions(civilityOptionsComputed);
    }, [setCivilityOptions]);

    useEffect(() => {
        const politesseOptionsComputed: OptionsProps = [];
        for (const value of enumKeys(Politesse)) {
            politesseOptionsComputed.push({
                label: t(`common.politesse.enum.${Politesse[value]}`),
                value: Politesse[value],
            });
        }
        setPolitesseOptions(politesseOptionsComputed);
    }, [setPolitesseOptions]);

    useEffect(() => {
        const fetchData = async (id: string) => {
            try {
                const descendantIdsFetched = await getDescendantIdsRequest(id);
                setDescendantIds(descendantIdsFetched);
            } catch (error) {
                toast.error(t('contact-form.descendants-fetch-error'));
            }
        };

        if (defaultValues?.id) {
            fetchData(defaultValues.id);
        }
    }, []);

    useEffect(() => {
        setParentOptions(
            contactList
                // During update, remove the contact himself of the list of parent list
                .filter((contact: Contact) => contact.id !== defaultValues?.id)
                .filter((contact: Contact) => !descendantIds.length || !descendantIds.includes(contact.id))
                .map((contact: Contact) => ({
                    label: `${contact.firstName} ${contact.lastName}`,
                    value: contact.id,
                })),
        );
    }, [setParentOptions, descendantIds]);

    const onClickToPremiumField = () => {
        if (isPremium === false) {
            closeModalStore();
            history.push('/ask-subscription');
        }
    };

    return (
        <FormProvider {...methods}>
            <form onSubmit={onSubmit}>
                <SSpacedColumn>
                    <SelectInput
                        control={control}
                        label={t('common.civility.label')}
                        name="civility"
                        options={civilityOptions}
                        placeholder={t('common.civility.placeholder')}
                        semi={!isMobile}
                        required
                    />
                    <SelectInput
                        control={control}
                        label={t('common.politesse.label')}
                        name="politesse"
                        options={politesseOptions}
                        placeholder={t('common.politesse.placeholder')}
                        semi={!isMobile}
                    />
                </SSpacedColumn>
                <SSpacedColumn>
                    <TextInput
                        label={t('common.first-name.label')}
                        name="firstName"
                        placeholder={t('common.first-name.placeholder')}
                        semi={!isMobile}
                    />
                    <TextInput
                        label={t('common.last-name.label')}
                        name="lastName"
                        placeholder={t('common.last-name.placeholder')}
                        semi={!isMobile}
                        transform={
                            (value: string) =>
                                value
                                    .toUpperCase()
                                    .replace(/[^A-ZÀÁÂÃÄÅÆÇÈÉÊËÌÍÎÏÐÑÒÓÔÕÖØÙÚÛÜÝÞŸ\s'-]/g, '') // Only allow letters, spaces, hyphens, and apostrophes
                                    .replace(/\s+/g, ' ') // Replace multiple spaces with single space
                        }
                        required
                    />
                </SSpacedColumn>
                <SSpacedColumn>
                    <TextInput
                        label={t('common.mobile-number.label')}
                        name="mobileNumber"
                        pattern={{ message: t(REGEXP.PHONE_NUMBER.message), value: REGEXP.PHONE_NUMBER.value }}
                        placeholder={t('common.mobile-number.placeholder')}
                        semi={!isMobile}
                    />
                    <TextInput
                        label={t('common.phone-number.label')}
                        name="phoneNumber"
                        pattern={{ message: t(REGEXP.PHONE_NUMBER.message), value: REGEXP.PHONE_NUMBER.value }}
                        placeholder={t('common.phone-number.placeholder')}
                        semi={!isMobile}
                    />
                </SSpacedColumn>
                <TextInput
                    label={t('common.email.label')}
                    name="email"
                    pattern={{ message: t(REGEXP.EMAIL.message), value: REGEXP.EMAIL.value }}
                    placeholder={t('common.email.placeholder')}
                    semi={!isMobile}
                    type="email"
                />
                <SSpacedColumn>
                    <TextInput
                        label={t('common.job.label')}
                        name="job"
                        placeholder={t('common.job.placeholder')}
                        semi={!isMobile}
                    />
                    <TextInput
                        label={t('common.company.label')}
                        name="company"
                        placeholder={t('common.company.placeholder')}
                        semi={!isMobile}
                    />
                </SSpacedColumn>
                <TextInput
                    label={t('common.description.label')}
                    name="comments"
                    placeholder={t('common.description.placeholder')}
                    isTextArea
                />
                <TagsInput
                    control={control as any}
                    isPremium={isPremium}
                    name="tags"
                    onClickToPremiumField={onClickToPremiumField}
                />
                <ResourcesInput name="resources" />
                <SelectInput
                    control={control}
                    helper={t('common.intermediary.helper-select-input')}
                    isDisabled={disabledFields && disabledFields.includes(DisabledField.ParentId)}
                    label={t('common.intermediary.label')}
                    name="parentId"
                    options={parentOptions}
                    placeholder={t('common.intermediary.placeholder')}
                    semi={!isMobile}
                />
                <BaseModal.BottomActions>
                    <Button onClick={() => closeModal()} variant="text">
                        {t('common.cancel')}
                    </Button>
                    <Button submit>{defaultValues?.id ? t('common.update') : t('common.create')}</Button>
                </BaseModal.BottomActions>
            </form>
        </FormProvider>
    );
};

export default ContactForm;
